import { useEffect, useRef, useState } from 'react';

const FEATURES = [
  {
    icon: 'ri-database-2-line',
    title: 'Data-Driven Strategy',
    titleKo: '데이터 기반 전략',
    desc: '모든 의사결정은 데이터에서 시작됩니다.\n수치로 전략을 설계합니다.',
  },
  {
    icon: 'ri-line-chart-line',
    title: 'ROAS Optimization',
    titleKo: 'ROAS 극대화',
    desc: '광고비 낭비 없이 수익률을 높입니다.\n지속적인 분석과 최적화로 실현합니다.',
  },
  {
    icon: 'ri-pulse-line',
    title: 'Real-Time Tracking',
    titleKo: '실시간 성과 추적',
    desc: '24시간 캠페인 지표를 모니터링합니다.\n이상 징후를 즉시 감지하고 대응합니다.',
  },
  {
    icon: 'ri-focus-3-line',
    title: 'Conversion-Focused',
    titleKo: '전환 중심 실행',
    desc: '클릭이 아닌 구매로 이어지게 합니다.\n모든 역량을 전환 최적화에 집중합니다.',
  },
];

const STEPS = [
  {
    num: '01',
    label: 'Analyze',
    labelKo: '데이터 분석',
    icon: 'ri-bar-chart-grouped-line',
    desc: '광고 구조와 전환 데이터를\n정밀하게 분석합니다.',
  },
  {
    num: '02',
    label: 'Strategy',
    labelKo: '전략 설계',
    icon: 'ri-map-2-line',
    desc: '목표 ROAS 기반으로\n채널별 예산과 전략을 수립합니다.',
  },
  {
    num: '03',
    label: 'Launch',
    labelKo: '캠페인 런칭',
    icon: 'ri-rocket-2-line',
    desc: '최적화된 소재와 타겟으로\n캠페인을 전면 집행합니다.',
  },
  {
    num: '04',
    label: 'Optimize',
    labelKo: '지속 최적화',
    icon: 'ri-refresh-line',
    desc: '실시간 지표로 입찰·소재·타겟을\n매일 최적화합니다.',
  },
  {
    num: '05',
    label: 'Scale',
    labelKo: '성과 확장',
    icon: 'ri-arrow-up-line',
    desc: '검증된 캠페인을 스케일업해\n매출을 폭발적으로 성장시킵니다.',
  },
];

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

export default function PerformanceSection() {
  const header = useInView();
  const cards = useInView(0.1);

  return (
    <section
      id="performance-system"
      className="relative py-14 md:py-28 bg-[#020409] overflow-hidden"
      style={{ fontFamily: "'Space Grotesk', 'Noto Sans KR', sans-serif" }}
    >
      {/* ── Background ── */}
      <div className="absolute inset-0 pointer-events-none select-none">
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[600px] rounded-full"
          style={{ background: 'radial-gradient(ellipse, rgba(0,255,135,0.04) 0%, transparent 65%)' }}
        />
        <div
          className="absolute inset-0 opacity-[0.022]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,.15) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.15) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
          }}
        />
      </div>
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/8 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-white/8 to-transparent" />

      <div className="relative z-10 max-w-6xl mx-auto px-5 md:px-8">

        {/* ── Header ── */}
        <div
          ref={header.ref}
          className="mb-10 md:mb-20 transition-all duration-700"
          style={{
            opacity: header.visible ? 1 : 0,
            transform: header.visible ? 'translateY(0)' : 'translateY(24px)',
          }}
        >
          {/* Eyebrow */}
          <div className="flex items-center gap-2.5 mb-6 md:mb-10">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00FF87] animate-pulse flex-shrink-0" />
            <span className="text-[#00FF87]/60 text-xs font-bold tracking-[0.12em] md:tracking-[0.25em] uppercase whitespace-nowrap">
              Performance Marketing System
            </span>
            <span className="flex-1 h-px bg-gradient-to-r from-[#00FF87]/20 to-transparent min-w-0" />
          </div>

          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 md:gap-0">
            <h2
              className="font-black text-white leading-[1.18] md:leading-[1.08]"
              style={{
                fontSize: 'clamp(1.45rem, 5.5vw, 4.2rem)',
                letterSpacing: '-0.025em',
              }}
            >
              퍼포먼스 마케팅{' '}
              <span style={{ color: '#00FF87', textShadow: '0 0 60px rgba(0,255,135,0.4)' }}>
                시스템
              </span>
            </h2>
            <p className="text-white/35 text-sm md:text-base font-medium leading-relaxed md:text-right md:max-w-xs">
              We don&apos;t spend ads.
              <br className="block md:hidden" />
              {' '}<span className="text-white/65">We scale results.</span>
            </p>
          </div>
        </div>

        {/* ── 4 Feature Cards ── */}
        <div
          ref={cards.ref}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-12 md:mb-24"
        >
          {FEATURES.map((f, i) => (
            <div
              key={i}
              className="group relative rounded-2xl p-5 md:p-7 pb-6 md:pb-8 border border-white/[0.07] cursor-default"
              style={{
                background: 'rgba(255,255,255,0.025)',
                opacity: cards.visible ? 1 : 0,
                transform: cards.visible ? 'translateY(0)' : 'translateY(28px)',
                transition: `opacity 0.6s ease ${i * 90}ms, transform 0.6s ease ${i * 90}ms, border-color 0.3s, background 0.3s`,
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLElement).style.borderColor = 'rgba(0,255,135,0.28)';
                (e.currentTarget as HTMLElement).style.background = 'rgba(0,255,135,0.05)';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.07)';
                (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.025)';
              }}
            >
              {/* Corner glow */}
              <div
                className="absolute -top-10 -right-10 w-28 h-28 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{ background: 'radial-gradient(circle, rgba(0,255,135,0.13) 0%, transparent 70%)' }}
              />

              {/* Top accent line */}
              <div
                className="absolute top-0 left-0 right-0 h-[2px] rounded-t-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-400"
                style={{ background: 'linear-gradient(90deg, transparent, #00FF87, transparent)' }}
              />

              {/* Icon */}
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 md:mb-5 border border-white/[0.07] group-hover:border-[#00FF87]/30 transition-all duration-300"
                style={{ background: 'rgba(255,255,255,0.04)' }}
              >
                <i className={`${f.icon} text-xl text-white/30 group-hover:text-[#00FF87] transition-colors duration-300`} />
              </div>

              {/* Text */}
              <p className="text-[#00FF87]/45 text-[10px] font-bold tracking-[0.22em] uppercase mb-1.5 group-hover:text-[#00FF87]/70 transition-colors duration-300">
                {f.title}
              </p>
              <h3 className="font-black text-white/75 group-hover:text-white text-base mb-2 md:mb-3 leading-snug transition-colors duration-300">
                {f.titleKo}
              </h3>
              <p className="text-white/30 group-hover:text-white/55 text-xs md:text-sm leading-[1.9] transition-colors duration-300 break-words">
                {f.desc.split('\n').map((line, li) => (
                  <span key={li}>
                    {li > 0 && <br />}
                    {line}
                  </span>
                ))}
              </p>
            </div>
          ))}
        </div>

        {/* ── 5-Step Process ── */}
        <div id="process">

          {/* Sub-header — same pattern as main header */}
          <div className="mb-10 md:mb-16">
            {/* Eyebrow */}
            <div className="flex items-center gap-2.5 mb-6 md:mb-10">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00FF87]/50 animate-pulse flex-shrink-0" />
              <span className="text-[#00FF87]/60 text-xs font-bold tracking-[0.12em] md:tracking-[0.25em] uppercase whitespace-nowrap">
                진행 프로세스
              </span>
              <span className="flex-1 h-px bg-gradient-to-r from-[#00FF87]/20 to-transparent min-w-0" />
            </div>

            {/* Title */}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 md:gap-0">
              <h2
                className="font-black text-white leading-[1.18] md:leading-[1.08]"
                style={{
                  fontSize: 'clamp(1.45rem, 5.5vw, 4.2rem)',
                  letterSpacing: '-0.025em',
                }}
              >
                5단계로 완성되는<br />
                <span style={{ color: '#00FF87', textShadow: '0 0 60px rgba(0,255,135,0.4)' }}>
                  성과 시스템
                </span>
              </h2>
              <p className="text-white/35 text-sm md:text-base font-medium leading-relaxed md:text-right md:max-w-xs">
                We don&apos;t just plan.{' '}
                <span className="text-white/65">We execute.</span>
              </p>
            </div>
          </div>

          {/* Step cards — timeline style */}
          <div className="relative">
            {/* Desktop connector line */}
            <div
              className="hidden lg:block absolute top-[52px] left-[10%] right-[10%] h-px pointer-events-none"
              style={{ background: 'linear-gradient(90deg, transparent, rgba(0,255,135,0.15) 20%, rgba(0,255,135,0.15) 80%, transparent)' }}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4">
              {STEPS.map((s, i) => (
                <div
                  key={i}
                  className="group relative rounded-2xl border border-white/[0.07] cursor-default flex flex-col"
                  style={{
                    background: 'rgba(255,255,255,0.02)',
                    transition: 'border-color 0.3s, background 0.3s',
                  }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLElement).style.borderColor = 'rgba(0,255,135,0.3)';
                    (e.currentTarget as HTMLElement).style.background = 'rgba(0,255,135,0.05)';
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.07)';
                    (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.02)';
                  }}
                >
                  {/* Corner glow */}
                  <div
                    className="absolute -top-10 -right-10 w-32 h-32 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                    style={{ background: 'radial-gradient(circle, rgba(0,255,135,0.12) 0%, transparent 70%)' }}
                  />
                  {/* Top accent */}
                  <div
                    className="absolute top-0 left-0 right-0 h-[2px] rounded-t-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-400"
                    style={{ background: 'linear-gradient(90deg, transparent, #00FF87, transparent)' }}
                  />
                  {/* Card top: icon circle + step number */}
                  <div className="relative flex items-center justify-between px-5 pt-5 pb-4 md:px-6 md:pt-6">
                    <div
                      className="relative w-[42px] h-[42px] rounded-full flex items-center justify-center border transition-all duration-300 group-hover:border-[#00FF87]/50 flex-shrink-0"
                      style={{ background: 'rgba(0,255,135,0.07)', border: '1px solid rgba(0,255,135,0.2)' }}
                    >
                      <i className={`${s.icon} text-base text-white/35 group-hover:text-[#00FF87] transition-colors duration-300`} />
                      <div className="absolute inset-[-4px] rounded-full border border-[#00FF87]/0 group-hover:border-[#00FF87]/20 transition-all duration-400" />
                    </div>
                    <span
                      className="font-black leading-none select-none"
                      style={{
                        fontSize: 'clamp(2.4rem, 5vw, 3.2rem)',
                        color: 'rgba(255,255,255,0.04)',
                        fontFamily: "'Space Grotesk', sans-serif",
                        letterSpacing: '-0.05em',
                        lineHeight: 1,
                      }}
                    >
                      {s.num}
                    </span>
                  </div>
                  <div className="mx-5 md:mx-6 h-px bg-white/[0.05] mb-4" />
                  <div className="px-5 pb-7 md:px-6 md:pb-8 flex-1">
                    <p className="text-[#00FF87]/40 text-[10px] font-bold tracking-[0.22em] uppercase mb-1.5 group-hover:text-[#00FF87]/65 transition-colors duration-300">
                      {s.label}
                    </p>
                    <h3 className="font-black text-white/70 group-hover:text-white text-sm md:text-base mb-2.5 leading-snug transition-colors duration-300">
                      {s.labelKo}
                    </h3>
                    <p className="text-white/30 group-hover:text-white/55 text-xs md:text-sm leading-[1.9] transition-colors duration-300 break-words">
                      {s.desc.split('\n').map((line, li) => (
                        <span key={li}>
                          {li > 0 && <br />}
                          {line}
                        </span>
                      ))}
                    </p>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className="hidden lg:flex absolute -right-[11px] top-[31px] w-5 h-5 items-center justify-center z-10">
                      <i className="ri-arrow-right-s-line text-[#00FF87]/25 text-lg" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Bottom CTA strip ── */}
        <div
          className="mt-10 md:mt-20 relative rounded-2xl border border-white/[0.07] overflow-hidden"
          style={{ background: 'rgba(255,255,255,0.025)' }}
        >
          {/* BG glow */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: 'radial-gradient(ellipse at 20% 50%, rgba(0,255,135,0.05) 0%, transparent 60%)' }}
          />
          <div
            className="absolute top-0 left-0 right-0 h-[1.5px]"
            style={{ background: 'linear-gradient(90deg, transparent, rgba(0,255,135,0.3), transparent)' }}
          />

          <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-5 md:gap-6 py-7 md:py-9 px-6 md:px-10">
            <div>
              <p className="text-[#00FF87]/50 text-xs font-bold tracking-[0.2em] uppercase mb-2">
                Ready to start?
              </p>
              <p className="font-black text-white text-base md:text-xl leading-snug" style={{ letterSpacing: '-0.01em' }}>
                지금 바로 광고 진단을 받아보세요
              </p>
              <p className="text-white/30 text-sm mt-1.5 leading-relaxed">
                시스템이 준비됐습니다.<br />
                당신의 브랜드만 빠져있습니다.
              </p>
            </div>
            <button
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="group relative flex items-center justify-center gap-2.5 font-black text-black px-8 md:px-10 py-3.5 md:py-4 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap flex-shrink-0 text-sm md:text-base w-full md:w-auto overflow-hidden"
              style={{ background: '#00FF87', boxShadow: '0 0 40px rgba(0,255,135,0.22)' }}
            >
              <span
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{ background: 'linear-gradient(105deg, transparent 35%, rgba(255,255,255,0.22) 50%, transparent 65%)' }}
              />
              <span className="relative z-10">무료 진단 신청하기</span>
              <div className="relative z-10 w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-base group-hover:translate-x-0.5 transition-transform duration-200" />
              </div>
            </button>
          </div>
        </div>

      </div>
    </section>
  );
}
